import type { Monaco } from '@monaco-editor/react';

export const VANTAGE_MONACO_LIGHT_THEME = 'countercyclical-vantage-light';
export const VANTAGE_MONACO_DARK_THEME = 'countercyclical-vantage-dark';

type RgbColor = {
    r: number;
    g: number;
    b: number;
    a: number;
};

export interface VantageMonacoThemeColors {
    background: string;
    border: string;
    hoverBackground: string;
    lineHighlightBackground: string;
    primary: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
}

const clampRgbChannel = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

const toHexChannel = (value: number) => clampRgbChannel(value).toString(16).padStart(2, '0');

const rgbToHex = ({ r, g, b }: RgbColor) => `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;

const parseCssColor = (color: string): RgbColor | null => {
    const normalizedColor = color.trim();
    const hexMatch = normalizedColor.match(/^#([0-9a-f]{3,8})$/i);

    if (hexMatch) {
        const hex = hexMatch[1];
        const expand = (value: string) =>
            value
                .split('')
                .map((character) => character + character)
                .join('');
        const expandedHex = hex.length === 3 || hex.length === 4 ? expand(hex) : hex;
        const r = Number.parseInt(expandedHex.slice(0, 2), 16);
        const g = Number.parseInt(expandedHex.slice(2, 4), 16);
        const b = Number.parseInt(expandedHex.slice(4, 6), 16);
        const a = expandedHex.length >= 8 ? Number.parseInt(expandedHex.slice(6, 8), 16) / 255 : 1;

        return { r, g, b, a };
    }

    const rgbMatch = normalizedColor.match(/^rgba?\(([^)]+)\)$/i);

    if (!rgbMatch) {return null;}

    const channels = rgbMatch[1].split(',').map((channel) => channel.trim());
    const [r, g, b] = channels.slice(0, 3).map((channel) => Number.parseFloat(channel));
    const a = channels[3] === undefined ? 1 : Number.parseFloat(channels[3]);

    if ([r, g, b, a].some((channel) => Number.isNaN(channel))) {return null;}

    return { r, g, b, a };
};

const blendColor = (foreground: RgbColor, background: RgbColor): RgbColor => {
    const alpha = Math.max(0, Math.min(1, foreground.a));

    return {
        r: foreground.r * alpha + background.r * (1 - alpha),
        g: foreground.g * alpha + background.g * (1 - alpha),
        b: foreground.b * alpha + background.b * (1 - alpha),
        a: 1,
    };
};

export const toMonacoHexColor = (color: string, fallback: string, background?: string) => {
    const parsedColor = parseCssColor(color);
    const parsedFallback = parseCssColor(fallback);
    const parsedBackground = background ? parseCssColor(background) : null;
    const resolvedColor = parsedColor ?? parsedFallback;

    if (!resolvedColor) {return fallback;}

    if (resolvedColor.a < 1) {
        return rgbToHex(
            blendColor(resolvedColor, parsedBackground ?? parsedFallback ?? { r: 255, g: 255, b: 255, a: 1 }),
        );
    }

    return rgbToHex(resolvedColor);
};

export const defineVantageMonacoTheme = (
    monacoInstance: Monaco,
    themeName: string,
    isDarkMode: boolean,
    colors: VantageMonacoThemeColors,
) => {
    monacoInstance.editor.defineTheme(themeName, {
        base: isDarkMode ? 'vs-dark' : 'vs',
        inherit: true,
        rules: [],
        colors: {
            'editor.background': colors.background,
            'editor.foreground': colors.text,
            'editor.lineHighlightBackground': colors.lineHighlightBackground,
            'editor.lineHighlightBorder': '#00000000',
            'editorLineNumber.foreground': colors.textTertiary,
            'editorWidget.background': colors.background,
            'editorWidget.border': colors.border,
            'editorSuggestWidget.background': colors.background,
            'editorSuggestWidget.foreground': colors.text,
            'editorSuggestWidget.border': colors.border,
            'editorSuggestWidget.highlightForeground': colors.primary,
            'editorSuggestWidget.focusHighlightForeground': colors.primary,
            'editorSuggestWidget.selectedBackground': colors.hoverBackground,
            'editorSuggestWidget.selectedForeground': colors.text,
            'editorSuggestWidget.selectedIconForeground': colors.text,
            'editorSuggestWidgetStatus.foreground': colors.textSecondary,
            'list.hoverBackground': colors.hoverBackground,
            'list.hoverForeground': colors.text,
            'list.focusBackground': colors.hoverBackground,
            'list.focusForeground': colors.text,
            'list.highlightForeground': colors.primary,
            'list.focusHighlightForeground': colors.primary,
        },
    });
};
